import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Layout from "../layout/default";
import { Block, Icon } from "../components";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { useApiClient } from "../ApiService/useApiClient";
import { dashboardApi } from "../ApiService/dashboardApi";
import { AppContext } from "../context/AppContext";
import { Link } from "react-router-dom";

const safeArr = (v) => (Array.isArray(v) ? v : []);
const isNum = (v) => typeof v === "number" && !Number.isNaN(v);
const money = (n) =>
  isNum(n)
    ? n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : "-";

const todayRangeUTC = () => {
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0));
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0));
  return { from: start.toISOString(), to: end.toISOString() };
};

const ymd = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toISOString().slice(0, 10);
};

const hm = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(11, 16);
  return d.toISOString().slice(11, 16);
};

const pill = (bg, text) => (
  <Badge bg={bg} className="px-2 py-1">
    {text}
  </Badge>
);

const visitStatusPill = (status, assignedStaffCount) => {
  const s = String(status || "").toLowerCase();
  if (s.includes("no staff") || assignedStaffCount === 0) return pill("danger", "No staff");
  if (s.includes("ready")) return pill("info", "Ready");
  if (s.includes("confirm")) return pill("primary", "Confirmed");
  if (s.includes("delay")) return pill("warning", "Delay");
  if (s.includes("complete")) return pill("success", "Completed");
  if (s.includes("cancel")) return pill("secondary", "Cancelled");
  return pill("dark", status || "Scheduled");
};

const priorityBadge = (p) => {
  const t = String(p || "").toLowerCase();
  if (t.includes("high")) return pill("danger", "High");
  if (t.includes("medium")) return pill("warning", "Medium");
  if (t.includes("low")) return pill("info", "Low");
  return pill("secondary", p || "Normal");
};

const takenBadge = (takenOrNot) => {
  const t = String(takenOrNot ?? "").toLowerCase().trim();
  if (t === "not taken") return pill("danger", "Not taken");
  if (t === "taken") return pill("success", "Taken");
  return pill("warning", "No record");
};

function StatTile({ title, value, sub, icon, variant = "primary" }) {
  return (
    <Card className="h-100 dashboard-tile">
      <Card.Body className="d-flex align-items-start justify-content-between">
        <div>
          <div className="small text-muted">{title}</div>
          <div className="fs-3 fw-bold">{value ?? "-"}</div>
          {sub ? <div className="small text-muted mt-1">{sub}</div> : null}
        </div>
        <div className={`tile-icon bg-${variant}-subtle text-${variant} rounded-3 p-2`}>
          <Icon name={icon} />
        </div>
      </Card.Body>
    </Card>
  );
}

function SectionHeader({ title, right }) {
  return (
    <div className="d-flex align-items-center justify-content-between mb-2">
      <div className="fw-bold">{title}</div>
      <div>{right}</div>
    </div>
  );
}

function CompactEmpty({ text }) {
  return (
    <div className="small text-muted d-flex align-items-center gap-2">
      <Icon name="info" /> {text}
    </div>
  );
}

export default function DashboardModern() {
  const api = useApiClient();
  const dash = useMemo(() => dashboardApi(api), [api]);
  const { SiteConfig } = useContext(AppContext);

  const timezone = "Europe/London";

  const { from: defaultFrom, to: defaultTo } = useMemo(() => todayRangeUTC(), []);
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);

  const [onlyExceptions, setOnlyExceptions] = useState(false);

  const [loadingTop, setLoadingTop] = useState(true);
  const [loadingVisits, setLoadingVisits] = useState(true);
  const [error, setError] = useState(null);

  // data
  const [summary, setSummary] = useState(null);
  const [visits, setVisits] = useState({ total: 0, items: [] });
  const [exceptions, setExceptions] = useState({ items: [] });
  const [highRisk, setHighRisk] = useState({ items: [] });
  const [reviews, setReviews] = useState({ overdue: [], dueSoon: [] });
  const [capacity, setCapacity] = useState({ counts: {}, onDuty: [], overtimeRisk: [] });
  const [finance, setFinance] = useState(null);

  // ✅ Missed medication
  const [missedMed, setMissedMed] = useState({ items: [] });

  // ✅ Assign modal (loads staff list inside modal)
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignTaskId, setAssignTaskId] = useState(null);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState(null);
  const [availableStaff, setAvailableStaff] = useState([]);

  const paramsBase = useMemo(
    () => ({
      from,
      to,
      timezone,
      branchId: SiteConfig?.CURRENT_BRANCH_ID,
      teamId: SiteConfig?.CURRENT_TEAM_ID,
    }),
    [from, to, timezone, SiteConfig]
  );

  const loadTop = useCallback(async () => {
    setLoadingTop(true);
    setError(null);
    try {
      const [s, ex, hr, rv, cap, fin, mm] = await Promise.all([
        dash.summary(paramsBase),
        dash.exceptions(paramsBase),
        dash.highRiskCustomers(paramsBase),
        dash.careplanReviews({ ...paramsBase, days: 14 }),
        dash.staffCapacity(paramsBase),
        dash.finance(paramsBase),
        dash.missedMedication(paramsBase), // ✅ new
      ]);

      setSummary(s.data ?? null);
      setExceptions(ex.data ?? { items: [] });
      setHighRisk(hr.data ?? { items: [] });
      setReviews(rv.data ?? { overdue: [], dueSoon: [] });
      setCapacity(cap.data ?? { counts: {}, onDuty: [], overtimeRisk: [] });
      setFinance(fin.data ?? null);
      setMissedMed(mm.data ?? { items: [] });
    } catch (e) {
      setError(e);
    } finally {
      setLoadingTop(false);
    }
  }, [dash, paramsBase]);

  const loadVisits = useCallback(async () => {
    setLoadingVisits(true);
    setError(null);
    try {
      const res = await dash.visits({ ...paramsBase, onlyExceptions });
      setVisits(res.data ?? { total: 0, items: [] });
    } catch (e) {
      setError(e);
    } finally {
      setLoadingVisits(false);
    }
  }, [dash, paramsBase, onlyExceptions]);

  useEffect(() => {
    loadTop();
  }, [loadTop]);

  useEffect(() => {
    loadVisits();
  }, [loadVisits]);

  const refresh = async () => {
    await Promise.all([loadTop(), loadVisits()]);
  };

  // Adjust these routes if needed:
  const visitLink = (visitId) => `/jobsheet/${visitId}`;
  const customerLink = (customerId) => `/client/${customerId}`;
  const carePlanLink = (carePlanId) => `/careplans/${carePlanId}`;

  // ✅ Load available staff for a taskId
  const fetchAvailableStaff = useCallback(
    async (taskId) => {
      if (!taskId) return;
      setAssignLoading(true);
      setAssignError(null);
      setAvailableStaff([]);

      try {
        const url = `HMCareTask/getavailablestaff/${taskId}`;
        const res = await api.get(url);
        setAvailableStaff(safeArr(res.data));
      } catch (e) {
        setAssignError(e);
      } finally {
        setAssignLoading(false);
      }
    },
    [api]
  );

  const openAssignModal = async (taskId) => {
    setAssignTaskId(taskId);
    setAssignModalOpen(true);
    await fetchAvailableStaff(taskId);
  };

  const closeAssignModal = () => {
    setAssignModalOpen(false);
    setAssignTaskId(null);
    setAvailableStaff([]);
    setAssignError(null);
    setAssignLoading(false);
  };

  const assignStaffToTask = async (taskId, staff) => {
    try {
      setAssignLoading(true);
      setAssignError(null);

      const sURL = "HMCareTask/staff/assign/";
      const jsonData = {
        taskID: taskId,
        agentID: staff.id ?? staff.agentId ?? staff.staffId,
        type: "add",
      };

      await api.post(sURL, jsonData);

      closeAssignModal();
      refresh();
    } catch (e) {
      setAssignError(e);
    } finally {
      setAssignLoading(false);
    }
  };

  return (
    <Layout title="Dashboard" content="container">
      <Block>
        {/* Header */}
        <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
          <div>
            <div className="h4 mb-1">Operations Dashboard</div>
            <div className="small text-muted">
              {ymd(from)} • {timezone}
            </div>
          </div>

          <div className="d-flex gap-2 align-items-center">
            <Form.Check
              type="switch"
              id="exceptionsOnly"
              label="Exceptions only"
              checked={onlyExceptions}
              onChange={(e) => setOnlyExceptions(e.target.checked)}
            />
            <Button variant="outline-secondary" onClick={refresh} disabled={loadingTop || loadingVisits}>
              <Icon name="refresh" /> <span>Refresh</span>
            </Button>
          </div>
        </div>

        {error ? (
          <Alert variant="danger">
            {String(error?.response?.data?.message || error?.message || error)}
          </Alert>
        ) : null}

        {/* Summary tiles */}
        {loadingTop ? (
          <div className="py-3">
            <Spinner animation="border" size="sm" /> <span className="text-muted ms-2">Loading dashboard…</span>
          </div>
        ) : (
          <Row className="g-3 mb-4">
            <Col xs={12} md={6} lg={3}>
              <StatTile
                title="Visits today"
                value={`${summary?.visits?.completed ?? 0}/${summary?.visits?.scheduled ?? 0}`}
                sub={`${summary?.visits?.inProgress ?? 0} in progress • ${summary?.visits?.missed ?? 0} missed`}
                icon="calendar"
                variant="primary"
              />
            </Col>

            <Col xs={12} md={6} lg={3}>
              <StatTile
                title="Late starts"
                value={summary?.visits?.lateStarts ?? 0}
                sub={`${summary?.visits?.unassigned ?? 0} unassigned`}
                icon="alarm"
                variant="warning"
              />
            </Col>

            <Col xs={12} md={6} lg={3}>
              <StatTile
                title="Staff on duty"
                value={summary?.staff?.onDutyNow ?? 0}
                sub={`${summary?.staff?.availableNow ?? 0} available • ${summary?.staff?.offSickToday ?? 0} off sick`}
                icon="users"
                variant="info"
              />
            </Col>

            <Col xs={12} md={6} lg={3}>
              <StatTile
                title="Missed medications"
                value={safeArr(missedMed?.items).length}
                sub="Today"
                icon="report"
                variant="danger"
              />
            </Col>
          </Row>
        )}

        <Row className="g-3">
          {/* Live visits board */}
          <Col xs={12} lg={8}>
            <Card className="h-100">
              <Card.Body>
                <SectionHeader
                  title="Live Visits Board"
                  right={
                    <div className="small text-muted">
                      {loadingVisits ? "Loading…" : `${visits?.total ?? safeArr(visits?.items).length} visits`}
                    </div>
                  }
                />

                {loadingVisits ? (
                  <div className="py-3">
                    <Spinner animation="border" size="sm" /> <span className="text-muted ms-2">Loading visits…</span>
                  </div>
                ) : safeArr(visits?.items).length === 0 ? (
                  <CompactEmpty text="No visits returned for this range." />
                ) : (
                  <Table responsive hover className="align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>Start–End</th>
                        <th>Status</th>
                        <th>Assigned</th>
                        <th>Flags</th>
                      </tr>
                    </thead>
                    <tbody>
                      {safeArr(visits?.items).slice(0, 14).map((v) => {
                        const assigned = safeArr(v.assignedStaff);
                        const hasNoStaff = assigned.length === 0 || String(v.status || "").toLowerCase().includes("no staff");

                        return (
                          <tr key={v.visitId}>
                            <td>
                              <div className="fw-semibold d-flex align-items-center gap-2">
                                <span>{v.customerName}</span>
                                {v.hasCarePlan ? <Badge bg="success">Care Plan</Badge> : null}
                              </div>
                              <div className="small text-muted">
                                {v.postcode || ""} •{" "}
                                <Link to={customerLink(v.customerId)} className="text-decoration-none">
                                  Open profile
                                </Link>
                              </div>
                            </td>

                            <td className="small">
                              <div className="fw-semibold">
                                {hm(v.scheduledStart)}–{hm(v.scheduledEnd)}
                              </div>
                              <div className="text-muted">{ymd(v.scheduledStart)}</div>
                            </td>

                            <td>{visitStatusPill(v.status, assigned.length)}</td>

                            <td className="small">
                              {hasNoStaff ? <Badge bg="danger">Unassigned</Badge> : assigned.map((s) => s.name).join(", ")}
                            </td>

                            <td className="small">
                              <div className="d-flex flex-wrap gap-1">
                                {v.lateByMinutes > 0 ? <Badge bg="warning">{v.lateByMinutes}m late</Badge> : null}
                                {v.notesMissing ? <Badge bg="danger">Notes missing</Badge> : <Badge bg="success">Notes OK</Badge>}
                                {safeArr(v.riskFlags).length ? <Badge bg="dark">{safeArr(v.riskFlags).length} risk(s)</Badge> : <Badge bg="secondary">No risks</Badge>}
                              </div>

                              <div className="mt-1">
                                <Link to={visitLink(v.visitId)} className="small text-decoration-none">
                                  Open jobsheet
                                </Link>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Action required queue */}
          <Col xs={12} lg={4}>
            <Card className="h-100">
              <Card.Body>
                <SectionHeader title="Action Required" right={<span className="small text-muted">{safeArr(exceptions?.items).length} items</span>} />

                {safeArr(exceptions?.items).length === 0 ? (
                  <div className="small text-muted d-flex align-items-center gap-2">
                    <Icon name="check" /> No outstanding issues 🎉
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {safeArr(exceptions?.items).slice(0, 10).map((x, idx) => {
                      const entity = x.entity || {};
                      const isUnassignedVisit = x.type === "UnassignedVisit" && entity.visitId != null;

                      return (
                        <div key={idx} className="p-2 rounded-3 border">
                          <div className="d-flex justify-content-between align-items-start gap-2">
                            <div className="fw-semibold">{x.title}</div>
                            {priorityBadge(x.priority)}
                          </div>

                          <div className="small text-muted mt-1">{x.type}</div>

                          {safeArr(x.suggestions).length ? (
                            <div className="small mt-2">
                              <span className="text-muted">Suggested:</span>{" "}
                              {safeArr(x.suggestions)
                                .slice(0, 2)
                                .map((s) => s.name)
                                .join(", ")}
                            </div>
                          ) : null}

                          <div className="d-flex flex-wrap gap-2 mt-2">
                            {isUnassignedVisit ? (
                              <Button size="sm" variant="primary" onClick={() => openAssignModal(entity.visitId)}>
                                <Icon name="user" /> <span>Assign Staff</span>
                              </Button>
                            ) : null}

                            {entity.visitId != null ? (
                              <Link to={visitLink(entity.visitId)} className="small text-decoration-none">
                                Open visit
                              </Link>
                            ) : null}

                            {entity.customerId != null ? (
                              <Link to={customerLink(entity.customerId)} className="small text-decoration-none">
                                Open customer
                              </Link>
                            ) : null}

                            {entity.carePlanId != null ? (
                              <Link to={carePlanLink(entity.carePlanId)} className="small text-decoration-none">
                                Open care plan
                              </Link>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* ✅ Missed Medication Today */}
          <Col xs={12}>
            <Card>
              <Card.Body>
                <SectionHeader
                  title="Missed Medication Today"
                  right={<span className="small text-muted">{safeArr(missedMed?.items).length} items</span>}
                />

                {safeArr(missedMed?.items).length === 0 ? (
                  <CompactEmpty text="No missed medication for today. All good." />
                ) : (
                  <Table responsive hover className="align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>Medication</th>
                        <th>Schedule</th>
                        <th>Time</th>
                        <th>Status</th>
                        <th>Notes</th>
                        <th>Related Visits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {safeArr(missedMed?.items).slice(0, 20).map((m) => (
                        <tr key={m.usageId}>
                          <td>
                            <div className="fw-semibold">{m.customerName}</div>
                            <div className="small text-muted">
                              <Link to={customerLink(m.customerId)} className="text-decoration-none">
                                Open profile
                              </Link>
                            </div>
                          </td>

                          <td>
                            <div className="fw-semibold">{m.medicationName}</div>
                            {m.headerNotes ? <div className="small text-muted">{String(m.headerNotes).slice(0, 60)}</div> : null}
                          </td>

                          <td className="small">{m.medicationSchedule || "-"}</td>
                          <td className="small">
                            {ymd(m.usageDateTime)} <span className="text-muted">{hm(m.usageDateTime)}</span>
                          </td>

                          <td>{takenBadge(m.takenOrNot)}</td>

                          <td className="small">
                            {m.usageNotes ? (
                              <span title={m.usageNotes}>{String(m.usageNotes).slice(0, 70)}</span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>

                          <td className="small">
                            {safeArr(m.relatedTasks).length === 0 ? (
                              <span className="text-muted">—</span>
                            ) : (
                              <div className="d-flex flex-column gap-1">
                                {safeArr(m.relatedTasks).slice(0, 3).map((t) => (
                                  <div key={t.TaskId} className="d-flex align-items-center justify-content-between gap-2">
                                    <Link to={visitLink(t.taskId)} className="text-decoration-none">
                                      {t.ProName || `Visit ${t.taskId}`}
                                    </Link>
                                    <span className="text-muted">
                                      {hm(t.startTime)}–{hm(t.endTime)}
                                    </span>
                                    <span>{visitStatusPill(t.status, 1)}</span>
                                  </div>
                                ))}
                                {safeArr(m.relatedTasks).length > 3 ? (
                                  <span className="text-muted">+{safeArr(m.relatedTasks).length - 3} more</span>
                                ) : null}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Staff capacity + overtime */}
          <Col xs={12} lg={7}>
            <Card className="h-100">
              <Card.Body>
                <SectionHeader
                  title="Staff Capacity"
                  right={
                    <div className="small text-muted">
                      On duty: {capacity?.counts?.onDutyNow ?? 0} • Available: {capacity?.counts?.availableNow ?? 0} • Off sick:{" "}
                      {capacity?.counts?.offSickToday ?? 0}
                    </div>
                  }
                />
                {safeArr(capacity?.onDuty).length === 0 ? (
                  <CompactEmpty text="No staff returned." />
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    {safeArr(capacity?.onDuty).slice(0, 24).map((s) => (
                      <div key={s.staffId} className="border rounded-3 px-2 py-1 small">
                        <span className="fw-semibold">{s.name}</span> <span className="text-muted">• {s.status}</span>
                        {s.currentVisitId ? (
                          <span className="ms-2">
                            <Link to={visitLink(s.currentVisitId)} className="small text-decoration-none">
                              visit
                            </Link>
                          </span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} lg={5}>
            <Card className="h-100">
              <Card.Body>
                <SectionHeader title="Overtime Risk" right={<span className="small text-muted">This week</span>} />
                {safeArr(capacity?.overtimeRisk).length === 0 ? (
                  <CompactEmpty text="No overtime risk." />
                ) : (
                  safeArr(capacity?.overtimeRisk).slice(0, 10).map((s) => (
                    <div key={s.staffId} className="d-flex justify-content-between border rounded-3 p-2 mb-2">
                      <div className="fw-semibold">{s.name}</div>
                      <div className="text-end">
                        <Badge bg={String(s.risk || "").toLowerCase().includes("high") ? "danger" : "warning"}>
                          {s.risk}
                        </Badge>
                        <div className="small text-muted mt-1">{s.hoursThisWeek}h</div>
                      </div>
                    </div>
                  ))
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Finance snapshot */}
          {finance ? (
            <Col xs={12}>
              <Card>
                <Card.Body>
                  <SectionHeader title="Finance Snapshot" right={<span className="small text-muted">Today/Range</span>} />
                  <Row className="g-3">
                    <Col xs={12} md={3}>
                      <StatTile title="Delivered hours" value={finance.deliveredHours ?? "-"} sub="Total care delivered" icon="clock" variant="primary" />
                    </Col>
                    <Col xs={12} md={3}>
                      <StatTile title="Billable hours" value={finance.billableHours ?? "-"} sub="Total billable" icon="money" variant="success" />
                    </Col>
                    <Col xs={12} md={3}>
                      <StatTile title="Unbilled visits" value={finance.unbilledCompletedVisits ?? "-"} sub="Completed not invoiced" icon="file" variant="warning" />
                    </Col>
                    <Col xs={12} md={3}>
                      <Card className="h-100 dashboard-tile">
                        <Card.Body>
                          <div className="small text-muted">Invoice aging</div>
                          <div className="mt-2 small d-flex justify-content-between">
                            <span className="text-muted">Current</span>
                            <span className="fw-semibold">£{money(finance?.invoiceAging?.current)}</span>
                          </div>
                          <div className="mt-1 small d-flex justify-content-between">
                            <span className="text-muted">Over 30</span>
                            <span className="fw-semibold">£{money(finance?.invoiceAging?.over30)}</span>
                          </div>
                          <div className="mt-1 small d-flex justify-content-between">
                            <span className="text-muted">Over 60</span>
                            <span className="fw-semibold">£{money(finance?.invoiceAging?.over60)}</span>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          ) : null}
        </Row>

        {/* ✅ Assign Staff Modal */}
        <Modal show={assignModalOpen} onHide={closeAssignModal} centered backdrop="static">
          <Modal.Header closeButton>
            <Modal.Title>Assign Staff</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {assignError ? (
              <Alert variant="danger" className="mb-2">
                {String(assignError?.response?.data?.message || assignError?.message || assignError)}
              </Alert>
            ) : null}

            {assignLoading ? (
              <div className="py-2">
                <Spinner animation="border" size="sm" /> <span className="text-muted ms-2">Loading available staff…</span>
              </div>
            ) : availableStaff.length === 0 ? (
              <CompactEmpty text="No available staff returned for this visit." />
            ) : (
              <div className="d-flex flex-column gap-2">
                {availableStaff.map((s) => (
                  <div
                    key={s.id ?? s.staffId ?? s.agentId}
                    className="border rounded-3 p-2 d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <div className="fw-semibold">{s.name}</div>
                      {s.reason ? <div className="small text-muted">{s.reason}</div> : null}
                    </div>
                    <Button size="sm" variant="primary" onClick={() => assignStaffToTask(assignTaskId, s)} disabled={assignLoading}>
                      Assign
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="light" onClick={closeAssignModal} disabled={assignLoading}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Block>
    </Layout>
  );
}
